/**
 * Pure helpers for the unified task-chain data model.
 *
 * A task has shape:
 *   {
 *     id, type, title, category, description?, parent_id, order, status,
 *     depends_on: string[], notes?, createdAt, updatedAt,
 *     work? | decision? | purchase? | delivery? | scheduling? | qa? | approval?
 *   }
 *
 * These helpers are UI-agnostic; a CommonJS twin lives at functions/taskTree.js
 * so the Cloud Function uses the same semantics.
 */

export const TASK_TYPES = [
  'milestone',
  'work',
  'decision',
  'purchase',
  'delivery',
  'scheduling',
  'qa',
  'approval',
];

// Status values considered "done" for dependency and rollup purposes.
export const TERMINAL_STATUSES_BY_TYPE = {
  milestone: ['complete'],
  work: ['complete'],
  decision: ['complete'],
  purchase: ['complete', 'delivered'],
  delivery: ['delivered'],
  scheduling: ['complete'],
  qa: ['passed', 'na'],
  approval: ['approved'],
};

// Statuses that mean "actively moving" — used to surface in_progress rollup.
const ACTIVE_STATUSES = new Set([
  'in_progress',
  'ordered',
  'scheduled',
  'submitted',
  'needs_details',
]);

export function isTerminal(task) {
  if (!task) return false;
  const allowed = TERMINAL_STATUSES_BY_TYPE[task.type] || ['complete'];
  return allowed.includes(task.status);
}

export function isActive(task) {
  if (!task) return false;
  return ACTIVE_STATUSES.has(task.status);
}

function isFailedQA(task) {
  return task && task.type === 'qa' && task.status === 'failed';
}

export function indexById(tasks) {
  const map = new Map();
  for (const t of tasks || []) {
    if (t && t.id) map.set(t.id, t);
  }
  return map;
}

export function indexByParent(tasks) {
  const map = new Map();
  for (const t of tasks || []) {
    if (!t) continue;
    const key = t.parent_id || null;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => {
      const oa = typeof a.order === 'number' ? a.order : 0;
      const ob = typeof b.order === 'number' ? b.order : 0;
      if (oa !== ob) return oa - ob;
      const ca = a.createdAt || 0;
      const cb = b.createdAt || 0;
      if (ca !== cb) return ca - cb;
      return String(a.id).localeCompare(String(b.id));
    });
  }
  return map;
}

/**
 * Build a tree from a flat list. Returns roots (tasks whose parent_id is
 * null OR points to a missing task) with a nested `children[]` on each node.
 */
export function buildTree(tasks) {
  const byId = indexById(tasks);
  const byParent = indexByParent(tasks);

  function attachChildren(parentId) {
    return (byParent.get(parentId) || []).map((t) => ({
      ...t,
      children: attachChildren(t.id),
    }));
  }

  const roots = [];
  for (const t of tasks || []) {
    if (!t) continue;
    if (!t.parent_id || !byId.has(t.parent_id)) {
      roots.push({ ...t, children: attachChildren(t.id) });
    }
  }
  roots.sort((a, b) => {
    const oa = typeof a.order === 'number' ? a.order : 0;
    const ob = typeof b.order === 'number' ? b.order : 0;
    if (oa !== ob) return oa - ob;
    return (a.createdAt || 0) - (b.createdAt || 0);
  });
  return roots;
}

export function getDescendants(task, tasks) {
  const byParent = indexByParent(tasks);
  const out = [];
  const stack = [task.id];
  while (stack.length) {
    const id = stack.pop();
    const kids = byParent.get(id) || [];
    for (const k of kids) {
      out.push(k);
      stack.push(k.id);
    }
  }
  return out;
}

/**
 * Flatten the tree in depth-first order, annotating each task with its depth.
 */
export function flattenTasks(tasks) {
  const roots = buildTree(tasks);
  const out = [];
  function walk(node, depth) {
    const { children, ...rest } = node;
    out.push({ ...rest, depth });
    for (const c of children || []) walk(c, depth + 1);
  }
  for (const r of roots) walk(r, 0);
  return out;
}

/**
 * Rolled-up status for a task (milestones or any node with children).
 *   1. No children               -> own status
 *   2. Any descendant qa failed  -> 'blocked'
 *   3. Every descendant terminal -> 'complete'
 *   4. Any descendant active     -> 'in_progress'
 *   5. Otherwise                 -> 'pending'
 */
export function computeRollupStatus(task, tasks) {
  if (!task) return 'pending';
  const descendants = getDescendants(task, tasks);
  if (descendants.length === 0) return task.status || 'pending';
  if (descendants.some(isFailedQA)) return 'blocked';
  if (descendants.every(isTerminal)) return 'complete';
  if (descendants.some(isActive)) return 'in_progress';
  return 'pending';
}

/**
 * Blocked when any id in depends_on refers to a non-terminal task.
 * Missing referents are treated as terminal so orphan refs cannot permanently block.
 */
export function isBlocked(task, tasks) {
  if (!task || !Array.isArray(task.depends_on) || task.depends_on.length === 0) {
    return false;
  }
  const byId = indexById(tasks);
  return task.depends_on.some((id) => {
    const dep = byId.get(id);
    if (!dep) return false;
    return !isTerminal(dep);
  });
}

export function getBlockers(task, tasks) {
  if (!task || !Array.isArray(task.depends_on)) return [];
  const byId = indexById(tasks);
  return task.depends_on
    .map((id) => byId.get(id))
    .filter((t) => t && !isTerminal(t));
}

/**
 * Would setting task.parent_id = newParentId create a cycle in the parent tree?
 */
export function hasParentCycle(tasks, childId, newParentId) {
  if (!newParentId) return false;
  if (newParentId === childId) return true;
  const byId = indexById(tasks);
  let cursor = byId.get(newParentId);
  const seen = new Set();
  while (cursor) {
    if (cursor.id === childId) return true;
    if (seen.has(cursor.id)) return true;
    seen.add(cursor.id);
    cursor = cursor.parent_id ? byId.get(cursor.parent_id) : null;
  }
  return false;
}

/**
 * Would setting task.depends_on = proposed create a cycle in the dependency graph?
 */
export function hasDependencyCycle(tasks, taskId, proposedDependsOn) {
  if (!Array.isArray(proposedDependsOn) || proposedDependsOn.length === 0) return false;
  if (proposedDependsOn.includes(taskId)) return true;
  const byId = indexById(tasks);
  const stack = [...proposedDependsOn];
  const seen = new Set();
  while (stack.length) {
    const id = stack.pop();
    if (id === taskId) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    const t = byId.get(id);
    if (t && Array.isArray(t.depends_on)) {
      for (const d of t.depends_on) stack.push(d);
    }
  }
  return false;
}

export function stripOrphanDependencies(tasks, removedIds) {
  const removed = new Set(removedIds);
  return (tasks || []).map((t) => {
    if (!t || !Array.isArray(t.depends_on)) return t;
    if (!t.depends_on.some((id) => removed.has(id))) return t;
    return { ...t, depends_on: t.depends_on.filter((id) => !removed.has(id)) };
  });
}

export function collectSubtreeIds(task, tasks) {
  const ids = [task.id];
  for (const d of getDescendants(task, tasks)) ids.push(d.id);
  return ids;
}

/**
 * Group non-milestone tasks into "chains" — connected components of the
 * depends_on graph (treated as undirected). Within each chain, tasks are
 * topologically sorted (predecessors first) with stable ties on order /
 * createdAt / id. Milestones are excluded from the chain view; depends_on
 * edges pointing at missing tasks are dropped.
 *
 * Each chain is annotated with:
 *   - status: 'active' | 'blocked' | 'done'
 *       active  = at least one incomplete task is unblocked
 *       blocked = incomplete tasks exist but every one is blocked
 *       done    = every task is terminal
 *   - doneCount / totalCount
 *   - nextIncompleteIndex: index of the first non-terminal task, or the last
 *     index when the chain is fully done
 *
 * Chains are returned sorted active-first, then blocked, then done; ties
 * broken by the first task's order / createdAt.
 */
export function computeChains(tasks) {
  const nodes = (tasks || []).filter((t) => t && t.type !== 'milestone');
  if (nodes.length === 0) return [];
  const idSet = new Set(nodes.map((t) => t.id));
  const byId = new Map(nodes.map((t) => [t.id, t]));

  const parent = new Map();
  for (const t of nodes) parent.set(t.id, t.id);
  function find(x) {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r);
    while (parent.get(x) !== r) {
      const next = parent.get(x);
      parent.set(x, r);
      x = next;
    }
    return r;
  }
  function union(a, b) {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  }
  for (const t of nodes) {
    for (const dep of t.depends_on || []) {
      if (idSet.has(dep)) union(t.id, dep);
    }
  }

  function cmp(a, b) {
    const oa = typeof a.order === 'number' ? a.order : 0;
    const ob = typeof b.order === 'number' ? b.order : 0;
    if (oa !== ob) return oa - ob;
    const ca = a.createdAt || 0;
    const cb = b.createdAt || 0;
    if (ca !== cb) return ca - cb;
    return String(a.id).localeCompare(String(b.id));
  }

  const groups = new Map();
  for (const t of nodes) {
    const r = find(t.id);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(t);
  }

  const chains = [];
  for (const group of groups.values()) {
    const localIds = new Set(group.map((t) => t.id));
    const indeg = new Map();
    const out = new Map();
    for (const t of group) {
      indeg.set(t.id, 0);
      out.set(t.id, []);
    }
    for (const t of group) {
      for (const dep of t.depends_on || []) {
        if (localIds.has(dep)) {
          indeg.set(t.id, indeg.get(t.id) + 1);
          out.get(dep).push(t.id);
        }
      }
    }
    const ready = group.filter((t) => indeg.get(t.id) === 0).sort(cmp);
    const sorted = [];
    const seen = new Set();
    while (ready.length) {
      const next = ready.shift();
      if (seen.has(next.id)) continue;
      seen.add(next.id);
      sorted.push(next);
      for (const nid of out.get(next.id)) {
        indeg.set(nid, indeg.get(nid) - 1);
        if (indeg.get(nid) === 0) {
          ready.push(byId.get(nid));
          ready.sort(cmp);
        }
      }
    }
    // Defensive: if depends_on contains a cycle, append leftovers in stable order.
    if (sorted.length < group.length) {
      for (const t of [...group].sort(cmp)) {
        if (!seen.has(t.id)) sorted.push(t);
      }
    }

    const totalCount = sorted.length;
    const doneCount = sorted.filter(isTerminal).length;
    const firstIncomplete = sorted.findIndex((t) => !isTerminal(t));
    let status;
    if (doneCount === totalCount) {
      status = 'done';
    } else {
      const anyActionable = sorted.some((t) => !isTerminal(t) && !isBlocked(t, tasks));
      status = anyActionable ? 'active' : 'blocked';
    }
    chains.push({
      id: sorted[0].id,
      tasks: sorted,
      status,
      doneCount,
      totalCount,
      nextIncompleteIndex: firstIncomplete >= 0 ? firstIncomplete : totalCount - 1,
    });
  }

  const rank = { active: 0, blocked: 1, done: 2 };
  chains.sort((a, b) => {
    const ra = rank[a.status];
    const rb = rank[b.status];
    if (ra !== rb) return ra - rb;
    const ea = a.tasks[0];
    const eb = b.tasks[0];
    const oa = typeof ea.order === 'number' ? ea.order : 0;
    const ob = typeof eb.order === 'number' ? eb.order : 0;
    if (oa !== ob) return oa - ob;
    if ((ea.createdAt || 0) !== (eb.createdAt || 0)) {
      return (ea.createdAt || 0) - (eb.createdAt || 0);
    }
    return String(ea.id).localeCompare(String(eb.id));
  });
  return chains;
}
